import { isAfter, isBefore, isSameDay, startOfDay } from 'date-fns';

export type DateRange = { from: Date | null; to: Date | null };

export type RangeDayState =
  | 'range-start'
  | 'in-range'
  | 'range-end'
  | 'single-day'
  | 'preview'
  | null;

export const isCompleteRange = (range: DateRange): range is { from: Date; to: Date } =>
  range.from !== null && range.to !== null;

export const nextRange = (current: DateRange, day: Date): DateRange => {
  const picked = startOfDay(day);

  if (!current.from || current.to) return { from: picked, to: null };
  if (isBefore(picked, current.from)) return { from: picked, to: null };

  return { from: current.from, to: picked };
};

const isStrictlyBetween = (day: Date, start: Date, end: Date) =>
  isAfter(day, start) && isBefore(day, end);

export const resolveDayState = (
  day: Date,
  range: DateRange,
  hovered: Date | null,
): RangeDayState => {
  const { from, to } = range;
  if (!from) return null;

  const target = startOfDay(day);

  if (to) {
    if (isSameDay(from, to)) return isSameDay(target, from) ? 'single-day' : null;
    if (isSameDay(target, from)) return 'range-start';
    if (isSameDay(target, to)) return 'range-end';

    return isStrictlyBetween(target, from, to) ? 'in-range' : null;
  }

  if (isSameDay(target, from)) return 'range-start';
  if (!hovered) return null;

  const end = startOfDay(hovered);
  if (!isAfter(end, from)) return null;

  return isStrictlyBetween(target, from, end) || isSameDay(target, end) ? 'preview' : null;
};
